/**
 * The last step every generator takes: wrap the parts it actually decided —
 * who is flying, what is on the ground, what would count as winning — in the
 * fields that are the same for all of them.
 *
 * Those are all `MissionContext` restated (era, region, front, weather, side,
 * tier, realism, combat level) plus the id/seed pair, and writing them out per
 * generator was five copies of one object literal drifting apart. A generator
 * should read as the mission it describes and nothing else.
 */

import { frontLineFlak, homeDefenceFlak } from "./flak-placement.ts";
import type { MissionContext } from "./mission-options.ts";
import {
  WEATHER_IS_COSMETIC,
  type MissionBase,
  type MissionFlak,
  type MissionFlight,
  type MissionSpec,
  type MissionTarget,
} from "./mission-spec.ts";
import type { MissionType } from "./mission-types.ts";
import type { MissionObjective } from "./objectives.ts";
import type { RaidPlan } from "./raid.ts";

export interface MissionDraft {
  /** The catalog id; the spec's own id is this plus the seed. */
  readonly catalogId: string;
  readonly type: MissionType;
  readonly title: string;
  readonly flights: readonly MissionFlight[];
  readonly targets?: readonly MissionTarget[];
  /**
   * Batteries this mission *authors* — the guns under a balloon, say. The
   * standing belt along the front is added for every mission by
   * `assembleMission` and is not listed here.
   */
  readonly flak?: readonly MissionFlak[];
  readonly objectives: readonly MissionObjective[];
  readonly homeBase?: MissionBase;
  /** Only the raid family declares one. */
  readonly raid?: RaidPlan;
  readonly briefing: readonly string[];
  /** Appended to the note every generated mission carries about the sky being cosmetic. */
  readonly portNotes?: readonly string[];
}

/**
 * The batteries a mission gets for free, over and above whatever it authors
 * itself: a belt along the front where there is a front, and the home-defence
 * ring where there is not.
 *
 * The ring is centred on world `(0, 0, 0)` — the region's own anchor, the town
 * `flight/fly-region.ts` recentres the scenery on. That is the place a
 * home-defence map is defending, and it is dry ground by construction now
 * (`world/region-ground.ts`), which is what keeps the guns and the
 * searchlights that copy them out of the sea.
 */
function standingFlak(context: MissionContext, catalogId: string): readonly MissionFlak[] {
  if (context.hasFrontLine) {
    return frontLineFlak({ front: context.frontLine, salt: catalogId, seed: context.seed });
  }
  return homeDefenceFlak({
    centre: { x: 0, y: 0, z: 0 },
    // The defenders over England are Allied whichever side the player is on,
    // so the guns are "friendly" only when he is flying for them.
    side: context.nation === "allied" ? "friendly" : "hostile",
    salt: catalogId,
    seed: context.seed,
  });
}

export function assembleMission(context: MissionContext, draft: MissionDraft): MissionSpec {
  return {
    id: `${draft.catalogId}:${context.seed}`,
    type: draft.type,
    title: draft.title,
    seed: context.seed,
    era: context.era,
    region: context.region,
    frontLine: context.frontLine,
    weather: context.weather,
    playerNation: context.nation,
    playerFlightId: "player",
    tier: context.tier,
    realism: context.realism,
    combatLevel: context.combatLevel,
    flights: draft.flights,
    targets: draft.targets ?? [],
    // Every mission gets its region's standing batteries, because the original
    // puts nine a side into every scenario it ships; what a mission decides for
    // itself is the guns on its own objective (`flak-placement.ts`).
    flak: [...(draft.flak ?? []), ...standingFlak(context, draft.catalogId)],
    objectives: draft.objectives,
    homeBase: draft.homeBase,
    raid: draft.raid,
    briefing: draft.briefing,
    portNotes: [WEATHER_IS_COSMETIC, ...(draft.portNotes ?? [])],
  };
}
